import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Star, ArrowDownRight } from "lucide-react";

const HERO_IMG =
  "https://customer-assets-gfyr7b9c.emergentagent.net/job_berm-maintenance/artifacts/cpria4im_IMG_0103.jpeg";

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.35 + i * 0.12, ease: [0.16, 1, 0.3, 1] },
  }),
};

const Line = ({ children, i }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      variants={lineVariants}
      initial="hidden"
      animate="show"
      custom={i}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  const go = () =>
    document.querySelector("#services")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen pt-32 pb-16 md:pb-24 overflow-hidden"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="relative z-10 flex items-center gap-3 mb-8"
          >
            <span className="flex gap-0.5">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={14} className="fill-clay text-clay" />
              ))}
            </span>
            <span className="text-xs tracking-[0.28em] uppercase text-forest-muted">
              5.0 · 5 Reviews · Property Maintenance
            </span>
          </motion.div>

          <h1 className="relative z-10 font-serif font-medium text-forest-ink tracking-tighter leading-[0.88] text-[13vw] sm:text-[10.5vw] lg:text-[6.6vw]">
            <Line i={0}>SideBerms</Line>
            <Line i={1}>Property</Line>
            <Line i={2}><span className="italic text-forest">Maintenance.</span></Line>
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.9 }}
            className="relative z-10 mt-10 max-w-md"
          >
            <p className="text-forest-muted text-base md:text-lg leading-relaxed">
              Ricky, Jahrome and the team handle painting, ceilings, fences
              and lawns across the neighbourhood — honest quotes, tidy work,
              excellent communication.
            </p>
            <button
              data-testid="hero-quote-btn"
              onClick={() =>
                document.querySelector("#quote")?.scrollIntoView({ behavior: "smooth" })
              }
              className="group mt-8 inline-flex items-center gap-3 rounded-full bg-forest text-bone pl-6 pr-2 py-2 text-sm tracking-wide"
            >
              Request your quote
              <span className="grid place-items-center h-9 w-9 rounded-full bg-bone text-forest group-hover:rotate-45 transition-transform duration-400">
                <ArrowDownRight size={16} />
              </span>
            </button>
          </motion.div>
        </div>

        <div className="lg:col-span-5">
          <motion.div
            initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
            animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
            transition={{ duration: 1.2, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="relative aspect-[3/4] overflow-hidden rounded-sm border border-forest-ink/10"
          >
            <motion.img
              src={HERO_IMG}
              alt="Sunroom conservatory interior"
              style={{ y: imgY, scale: imgScale }}
              className="absolute inset-0 h-[115%] w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-forest-ink/60 via-forest-ink/10 to-forest-ink/20" />
            <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
              <span className="text-bone/90 text-xs tracking-[0.28em] uppercase">
                Interior · Exterior
              </span>
              <span className="font-serif text-bone text-3xl italic">est. locally</span>
            </div>
          </motion.div>
        </div>
      </div>

      <button
        onClick={go}
        className="hidden md:flex absolute bottom-8 left-1/2 -translate-x-1/2 items-center gap-2 text-forest-muted text-xs tracking-[0.28em] uppercase"
      >
        <span className="h-10 w-px bg-forest-muted/40 animate-pulse" />
        Scroll
      </button>
    </section>
  );
}
